import type { NotificationPayload } from '@services/interfaces/notification.interface';

/** Every failed request handler in this app pulls the backend's error message the
 *  same way; centralising it stops each call site from re-deriving its own unsafe
 *  `any` cast onto `HttpErrorResponse.error` (whose shape Angular leaves untyped).
 *  Checked structurally, not via `instanceof HttpErrorResponse`, so it also works
 *  against the plain `{ error: { message } }` doubles component specs throw. */
export const getHttpErrorMessage = (err: unknown, fallback: string): string => {
  const body = (err as { error?: { message?: unknown } } | null | undefined)?.error;
  const message = body?.message;
  return typeof message === 'string' && message.length > 0 ? message : fallback;
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
    case 'approved':
    case 'accepted':
      return 'green';
    case 'rejected':
    case 'cancelled':
      return 'red';
    case 'pending':
    case 'suspended':
      return 'orange';
    case 'in-use':
    case 'in-review':
      return 'blue';
    case 'completed':
      return 'purple';
    case 'completed-not-used':
      return 'gold';
    case 'completed-not-returned':
      return 'volcano';
    default:
      return 'default';
  }
};

const BOOKING_STATUS_LABELS: Record<string, string> = {
  accepted: 'Accepted',
  'in-use': 'In Use',
  completed: 'Completed',
  cancelled: 'Cancelled',
  'completed-not-used': 'Not used',
  'completed-not-returned': 'Not returned',
};

export const getBookingStatusLabel = (status: string): string =>
  BOOKING_STATUS_LABELS[status] ?? status;

/** Shared by the notification bell and the command dashboard's alarm panel
 *  — both render a speed_limit_exceeded notification's speed vs. limit. */
export const formatSpeedAlarmReading = (payload: NotificationPayload): string =>
  `${payload.speedKmh ?? '?'} / ${payload.limitKmh ?? '?'} km/h`;

/** Buckets a device's `cdmaSignal`. Real packets carry the 0–31 signal-quality
 *  index cellular modules report (31 = strongest, 99 = unknown — e.g. "31.0"),
 *  not a dBm figure. The scale is standard; these band edges are our own
 *  reading of it. */
export const calculateCdmaSignalStrength = (cdmaSignal: number) => {
  switch (true) {
    case !Number.isFinite(cdmaSignal) || cdmaSignal < 0 || cdmaSignal > 31:
      return 'OUT OF BOUND';
    case cdmaSignal >= 20:
      return 'EXCELLENT';
    case cdmaSignal >= 15:
      return 'GOOD';
    case cdmaSignal >= 10:
      return 'FAIR';
    case cdmaSignal >= 2:
      return 'POOR';
    default:
      return 'NO SIGNAL';
  }
};

/** The device reports voltage in decivolts (128 = 12.8V); show it as volts. */
export const formatVoltage = (decivolts: number | null | undefined): string =>
  decivolts == null ? '—' : `${(decivolts / 10).toFixed(1)} V`;

/** Reader-facing label for the raw `cdmaSignal` a device reports (see
 *  `calculateCdmaSignalStrength`), or an em dash when it's absent, unknown (99)
 *  or unreadable. */
export const formatCdmaSignal = (raw: string | null | undefined): string => {
  if (raw == null || raw === '') {
    return '—';
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return '—';
  }
  const label = calculateCdmaSignalStrength(n);
  if (label === 'OUT OF BOUND') {
    return '—';
  }
  return label.charAt(0) + label.slice(1).toLowerCase();
};
